import { useState, useEffect, useCallback } from "react";
import { authFetch } from "../api";

/**
 * ListingDebugModal — read-only provenance/diagnostics popup for a listing.
 * Opened from the "🐞 Debug" link in ListingEditModal. Answers "where did this come from
 * and why is it in this state?": identity (rp_id/listing_number/unit/bbl), sync timing,
 * SE status + verification `waiting_on`, a LIVE RealPlus presence check (ghost detection),
 * and the listing's + building's task_queue history with last error.
 */
export default function ListingDebugModal({ listingId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [rp, setRp] = useState(null);
  const [rpLoading, setRpLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    authFetch(`/listings/${listingId}/debug`)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e)); });
    return () => { alive = false; };
  }, [listingId]);

  const checkRp = useCallback(async () => {
    setRpLoading(true);
    setRp(null);
    try { setRp(await authFetch(`/listings/${listingId}/rp-check`)); }
    catch (e) { setRp({ error: String(e) }); }
    finally { setRpLoading(false); }
  }, [listingId]);

  // The headline signal — auto-run on open.
  useEffect(() => { checkRp(); }, [checkRp]);

  const fmt = (v) => (v === null || v === undefined || v === "" ? "—"
    : typeof v === "string" && v.length > 19 && v[10] === "T" ? v.slice(0, 19).replace("T", " ")
    : String(v));

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <span style={{ fontWeight: 700 }}>🐞 Debug — listing {listingId}</span>
          <button onClick={onClose} style={closeBtn}>&times;</button>
        </div>

        {err && <div style={{ ...banner, background: "#ffebee", color: "#c62828" }}>Failed to load: {err}</div>}
        {!data && !err && <div style={{ padding: 20, color: "#888" }}>Loading…</div>}

        {data && (
          <div style={body}>
            {/* RP presence — the headline */}
            <RpBanner rp={rp} loading={rpLoading} onRecheck={checkRp} />

            {/* waiting_on / staleness quick flags */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
              {data.verification?.waiting_on && (
                <Badge color="#e65100" bg="#fff3e0">⏳ Waiting on: {data.verification.waiting_on}</Badge>
              )}
              {data.verification?.verification_flags?.rp_404 && (
                <Badge color="#c62828" bg="#ffebee">RP 404 flagged</Badge>
              )}
              {data.classification?.active_dom > 180 && (
                <Badge color="#8d6e00" bg="#fff8e1">Stale: {data.classification.active_dom}d on market</Badge>
              )}
              {data.classification?.ownership_type !== data.classification?.building_ownership_type && (
                <Badge color="#5c3d99" bg="#f0ebfa">
                  Type ≠ building ({data.classification.ownership_type} vs {data.classification.building_ownership_type}
                  {data.classification.building_verified ? ", verified" : ", unverified"})
                </Badge>
              )}
            </div>

            <Section title="Identity">
              <KV k="rp_id" v={fmt(data.identity.rp_id)} />
              <KV k="rp_ids" v={JSON.stringify(data.identity.rp_ids)} />
              <KV k="listing_number" v={fmt(data.identity.listing_number)} />
              <KV k="listing_numbers" v={JSON.stringify(data.identity.listing_numbers)} />
              <KV k="property_id" v={fmt(data.identity.property_id)} />
              <KV k="unit_id" v={fmt(data.identity.unit_id)} />
              <KV k="unit_number" v={fmt(data.identity.unit_number)} />
              <KV k="address" v={`${data.identity.address}${data.identity.building_name ? ` (${data.identity.building_name})` : ""}`} />
              <KV k="neighborhood" v={fmt(data.identity.neighborhood)} />
              <KV k="bbl (b/blk/lot)" v={`${fmt(data.identity.bbl)}  (${fmt(data.identity.borough)}/${fmt(data.identity.block)}/${fmt(data.identity.lot)})`} />
            </Section>

            <Section title="Classification">
              <KV k="sale_or_rental" v={fmt(data.classification.sale_or_rental)} />
              <KV k="ownership_type" v={fmt(data.classification.ownership_type)} />
              <KV k="building ownership" v={`${fmt(data.classification.building_ownership_type)} ${data.classification.building_verified ? "(verified)" : "(unverified)"}`} />
              <KV k="listing_status" v={fmt(data.classification.listing_status)} />
              <KV k="internal_status" v={fmt(data.classification.internal_status)} />
              <KV k="active_dom" v={fmt(data.classification.active_dom)} />
            </Section>

            <Section title="Timing">
              <KV k="created (RP)" v={fmt(data.timing.created_date)} />
              <KV k="modified (RP)" v={fmt(data.timing.modified_date)} />
              <KV k="ingested (us)" v={fmt(data.timing.ingested_at)} />
              <KV k="updated_at" v={fmt(data.timing.updated_at)} />
              <KV k="updated_by" v={fmt(data.timing.updated_by)} />
            </Section>

            <Section title="StreetEasy">
              <KV k="se_listing_id" v={fmt(data.streeteasy.se_listing_id)} />
              <KV k="se_scraped_at" v={fmt(data.streeteasy.se_scraped_at)} />
              <KV k="se_status_checked_at" v={fmt(data.streeteasy.se_status_checked_at)} />
              <KV k="se_history_extracted_at" v={fmt(data.streeteasy.se_history_extracted_at)} />
              <KV k="se_building_url" v={data.streeteasy.se_building_url
                ? <a href={data.streeteasy.se_building_url} target="_blank" rel="noopener noreferrer">{data.streeteasy.se_building_url}</a>
                : "—"} />
            </Section>

            <Section title="Verification">
              <KV k="waiting_on" v={fmt(data.verification.waiting_on)} />
              <KV k="confidence" v={fmt(data.verification.verification_confidence)} />
              <KV k="verified_at" v={fmt(data.verification.verified_at)} />
              <KV k="verified_by" v={fmt(data.verification.verified_by)} />
              <KV k="flags" v={<code style={code}>{JSON.stringify(data.verification.verification_flags)}</code>} />
            </Section>

            {data.urls?.length > 0 && (
              <Section title={`URLs (${data.urls.length})`}>
                {data.urls.map((u, i) => (
                  <KV key={i} k={u.url_type} v={<a href={u.url} target="_blank" rel="noopener noreferrer">{u.url}</a>} />
                ))}
              </Section>
            )}

            <Section title={`Task queue (${data.tasks?.length || 0})`}>
              {(!data.tasks || data.tasks.length === 0) && <div style={{ color: "#999", fontSize: 12 }}>No tasks.</div>}
              {data.tasks?.length > 0 && (
                <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#888" }}>
                      <th style={th}>type</th><th style={th}>scope</th><th style={th}>status</th>
                      <th style={th}>att</th><th style={th}>enqueued</th><th style={th}>error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tasks.map((t) => (
                      <tr key={t.id} style={{ borderTop: "1px solid #eee" }}>
                        <td style={td}>{t.task_type}</td>
                        <td style={td}>{t.scope}</td>
                        <td style={{ ...td, fontWeight: 600, color: statusColor(t.status) }}>{t.status}</td>
                        <td style={td}>{t.attempts}/{t.max_attempts}</td>
                        <td style={td}>{fmt(t.enqueued_at)}</td>
                        <td style={{ ...td, color: "#c62828", maxWidth: 260, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {t.error || ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function RpBanner({ rp, loading, onRecheck }) {
  let content, bg, color;
  if (loading || !rp) { content = "Checking RealPlus…"; bg = "#eef2ff"; color = "#3949ab"; }
  else if (rp.error) { content = `RP check error: ${rp.error}`; bg = "#fff3e0"; color = "#e65100"; }
  else if (rp.checked === false) { content = `Not checked — ${rp.reason || "no rp_id"}`; bg = "#f5f5f5"; color = "#666"; }
  else if (rp.found === false) { content = `❌ GHOST — RealPlus 404 for rp_id ${rp.rp_id}. Purged from RP (stale/withdrawn).`; bg = "#ffebee"; color = "#c62828"; }
  else { content = `✅ Present in RealPlus (rp_id ${rp.rp_id}) — status: ${rp.rp_status || "?"}, listed: ${rp.datelisted || "?"}${rp.price ? `, price: ${rp.price}` : ""}`; bg = "#e8f5e9"; color = "#2e7d32"; }
  return (
    <div style={{ ...banner, background: bg, color, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
      <span>{content}</span>
      <button onClick={onRecheck} disabled={loading} style={{ ...miniBtn, borderColor: color, color }}>recheck</button>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#999", margin: "4px 0 3px" }}>{title}</div>
    <div>{children}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div style={{ display: "flex", fontSize: 12, padding: "1px 0" }}>
    <span style={{ width: 170, flexShrink: 0, color: "#888" }}>{k}</span>
    <span style={{ wordBreak: "break-word" }}>{v}</span>
  </div>
);

const Badge = ({ children, color, bg }) => (
  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: bg, color }}>{children}</span>
);

const statusColor = (s) => ({ done: "#2e7d32", pending: "#e65100", processing: "#3949ab", dead: "#c62828" }[s] || "#666");

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000 };
const panel = { background: "#fff", borderRadius: 8, width: 620, maxWidth: "94vw", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 30px rgba(0,0,0,0.3)" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #e0e0e0", fontSize: 14 };
const body = { padding: "12px 16px", overflowY: "auto" };
const banner = { padding: "8px 10px", borderRadius: 6, fontSize: 12, marginBottom: 10 };
const closeBtn = { background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#888", lineHeight: 1 };
const miniBtn = { fontSize: 10, padding: "1px 8px", border: "1px solid", borderRadius: 10, background: "#fff", cursor: "pointer", whiteSpace: "nowrap" };
const code = { fontSize: 11, background: "#f5f5f5", padding: "1px 4px", borderRadius: 3 };
const th = { padding: "2px 6px", fontWeight: 600 };
const td = { padding: "2px 6px", verticalAlign: "top" };
